// Rendering the AI's text: every {{module.metric.scope}} placeholder becomes the
// value the PLATFORM computed (from brief.figures — never the model), formatted
// by its unit and linked to the screen it comes from. A number the validator
// could not find in the data pack is marked ⚠. `{{key|abs}}` (the validator writes
// it after a word that already says "fell") shows the value without its sign.
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { AI } from "@/constants/testIds/ai";

const PLACEHOLDER = /\{\{\s*([a-z_]+\.[a-z0-9_]+\.[A-Za-z0-9_-]+)\s*(\|\s*abs\s*)?\}\}/g;
const NUMBER = /[0-9٠-٩۰-۹]+(?:[.,٫٬][0-9٠-٩۰-۹]+)*/g;
// Units shown without a suffix (t() turns an empty translation into the key itself).
const BARE_UNITS = new Set(["count", "qty"]);
// Arabic counted-noun forms: [singular, plural (3–10), singular with tanween (11–99)].
const AR_COUNTED = {
    days: ["يوم", "أيام", "يومًا"],
    minutes: ["دقيقة", "دقائق", "دقيقةً"],
    hours: ["ساعة", "ساعات", "ساعةً"],
    pcs: ["حبة", "حبات", "حبةً"],
};

/** «١١ يومًا», «٥ أيام», «١٠٠ يوم»: the counted noun agrees with the number (n mod 100). */
export function arCounted(unit, v) {
    const forms = AR_COUNTED[unit];
    if (!forms) return null;
    if (!Number.isInteger(v)) return forms[0];
    const r = Math.abs(v) % 100;
    return r >= 3 && r <= 10 ? forms[1] : r >= 11 ? forms[2] : forms[0];
}

export function fmtFigure(fig, lang, t, { abs = false } = {}) {
    if (!fig || fig.value === null || fig.value === undefined) return "—";
    const loc = lang === "ar" ? "ar-SA" : "en-US";
    const v = abs ? Math.abs(Number(fig.value)) : Number(fig.value);
    const unit = fig.unit;
    let digits = 0;
    if (unit === "pct") digits = 1;
    else if (unit === "SAR") digits = Math.abs(v) >= 1000 ? 0 : 2;
    else if (unit === "qty" || unit === "kg") digits = Number.isInteger(v) ? 0 : 2;
    const num = new Intl.NumberFormat(loc, { minimumFractionDigits: unit === "pct" ? 1 : 0, maximumFractionDigits: digits }).format(v);
    const u = BARE_UNITS.has(unit) || !unit ? "" : (lang === "ar" && arCounted(unit, v)) || t(`ai_unit_${unit}`);
    if (unit === "pct") return `${num}${u}`;
    return u ? `${num} ${u}` : num;
}

export function FigureChip({ fkey, fig, lang, t, abs = false }) {
    const label = fig ? (lang === "ar" ? fig.label_ar : fig.label_en) : fkey;
    const text = fmtFigure(fig, lang, t, { abs });
    const cls = "inline-flex items-baseline rounded-md bg-[color:var(--brand-orange-soft)] px-1.5 py-0.5 font-semibold text-[color:var(--ink)] tabular-nums whitespace-nowrap hover:underline";
    if (fig?.route) {
        return (
            <Link to={fig.route} title={label} data-testid={AI.figure} data-key={fkey} className={cls}>
                {text}
            </Link>
        );
    }
    return <span title={label} data-testid={AI.figure} data-key={fkey} className={cls}>{text}</span>;
}

function plainWithFlags(text, flagged, keyBase, t) {
    if (!flagged || !flagged.size) return text;
    const out = [];
    let last = 0;
    let i = 0;
    for (const m of text.matchAll(NUMBER)) {
        if (!flagged.has(m[0])) continue;
        if (m.index > last) out.push(text.slice(last, m.index));
        out.push(
            <mark key={`${keyBase}-f${i++}`} data-testid={AI.flag} title={t("ai_unverified")}
                  className="inline-flex items-center gap-0.5 rounded bg-[color:var(--warning-soft-bg,#fff4d6)] px-1 text-[color:var(--warning-soft-fg,#8a5a00)]">
                <AlertTriangle className="h-3 w-3" />{m[0]}
            </mark>
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

/** text: {ar, en}; flags: [{value, lang}] from the validator. */
export function RichText({ text, flags, figures, lang, t }) {
    const s = (text && (lang === "ar" ? text.ar || text.en : text.en || text.ar)) || "";
    const flagged = new Set((flags || []).filter((f) => f.lang === lang || !f.lang).map((f) => f.value));
    const parts = [];
    let last = 0;
    let n = 0;
    for (const m of s.matchAll(PLACEHOLDER)) {
        if (m.index > last) parts.push(<Fragment key={`t${n}`}>{plainWithFlags(s.slice(last, m.index), flagged, `t${n}`, t)}</Fragment>);
        parts.push(<FigureChip key={`f${n}`} fkey={m[1]} fig={(figures || {})[m[1]]} lang={lang} t={t} abs={!!m[2]} />);
        last = m.index + m[0].length;
        n += 1;
    }
    if (last < s.length) parts.push(<Fragment key="tail">{plainWithFlags(s.slice(last), flagged, "tail", t)}</Fragment>);
    return <>{parts}</>;
}

export const TONE_DOT = {
    good: "bg-emerald-500",
    bad: "bg-red-500",
    warn: "bg-amber-500",
    neutral: "bg-slate-400",
};

export function fmtTemplate(s, vars) {
    return Object.entries(vars || {}).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), s);
}

export function fmtWhen(iso, lang) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-GB", {
            timeZone: "Asia/Riyadh", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
